import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { MEDIA_TYPE_VIDEO } from '../../../util/listingMedia';
import { prefersNoAutoplay } from '../../../util/prefersNoAutoplay';
import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { IconArrowHead, ResponsiveImage } from '../../../components';

import HeroLoadSpinner, {
  currentHeroAlreadyLoaded,
  stillHasRequestedVariant,
  stillImageKey,
} from '../HeroLoadSpinner';
import { isActiveGallerySlide } from '../isActiveGallerySlide';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ImageCarousel.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  // Same as page gallery: thumbs are a static strip, not a swipe-pan rail.
  disableThumbnailSwipe: true,
  showFullscreenButton: false,
  slideDuration: 350,
  // Explicit: next on last wraps to first (library default, kept for intent).
  infinite: true,
};

/**
 * Dark full-viewport lightbox body (Modal child on listing pages).
 * Layout matches Console-style media viewer: counter, circular nav/close,
 * rounded main image, bottom thumbnails with orange active state.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array<propTypes.image>} [props.images] - Legacy; used when media is absent
 * @param {Array} [props.media] - Ordered media items (images + videos)
 * @param {Array<string>} props.imageVariants
 * @param {Array<string>} [props.thumbnailVariants]
 * @param {number} [props.initialIndex]
 * @returns {JSX.Element}
 */
const ImageCarousel = props => {
  const {
    rootClassName,
    className,
    images,
    media,
    imageVariants,
    thumbnailVariants,
    initialIndex = 0,
  } = props;
  const [currentIndex, setIndex] = useState(initialIndex);
  const [loadedVideoIndexes, setLoadedVideoIndexes] = useState(() => new Set([initialIndex]));
  const [loadedStillKeys, setLoadedStillKeys] = useState(() => new Set());
  const galleryRootRef = useRef(null);
  const intl = useIntl();
  // Poster-only under Save-Data / 2g / reduced-motion (same rule as listing cards).
  const noAutoplay = prefersNoAutoplay();
  const thumbVariants = thumbnailVariants || imageVariants;
  // Depend on the name, not the array. A new array with the same name must not
  // rebuild items — react-image-gallery resets to startIndex when items change.
  const thumbVariantName = thumbVariants?.[0];

  // Stable identity: react-image-gallery resets to startIndex whenever `items`
  // changes reference, which made rapid next clicks (and parent re-renders) jump back.
  const items = useMemo(() => {
    const mediaItems = media || (images || []).map(image => ({ type: 'image', image }));
    return mediaItems.map((mediaItem, i) => {
      const image = mediaItem.type === MEDIA_TYPE_VIDEO ? null : mediaItem.image;
      return {
        // We will only use the image resource, but react-image-gallery
        // requires the `original` key from each item.
        original: '',
        alt: intl.formatMessage(
          { id: 'ImageCarousel.imageAltText' },
          { index: i + 1, count: mediaItems.length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ImageCarousel.imageAltText' },
          { index: i + 1, count: mediaItems.length }
        ),
        thumbnail:
          mediaItem.type === MEDIA_TYPE_VIDEO
            ? mediaItem.video.posterUrl
            : image?.attributes?.variants?.[thumbVariantName],
        image,
        media: mediaItem,
        index: i,
      };
    });
  }, [media, images, intl, thumbVariantName]);

  const showThumbnails = items.length > 1;

  useEffect(() => {
    if (noAutoplay) {
      return;
    }
    setLoadedVideoIndexes(prev => {
      if (prev.has(currentIndex)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(currentIndex);
      return next;
    });
  }, [currentIndex, noAutoplay]);

  useEffect(() => {
    if (noAutoplay) {
      return;
    }
    const root = galleryRootRef.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('video[data-gallery-video="true"]').forEach(video => {
      const slide = video.closest('.image-gallery-slide');
      if (isActiveGallerySlide(slide) && video.src) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
    });
  }, [currentIndex, items.length, loadedVideoIndexes, noAutoplay]);

  const markStillLoaded = useCallback(key => {
    if (!key) {
      return;
    }
    setLoadedStillKeys(prev => {
      if (prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const renderItem = item => {
    const isVideo = item.media?.type === MEDIA_TYPE_VIDEO;
    return (
      <div className={css.imageWrapper}>
        <div className={css.itemCentering}>
          {isVideo ? (
            <video
              className={css.item}
              // Save-Data / 2g / reduced-motion: poster only — never attach the full mp4.
              src={
                !noAutoplay && loadedVideoIndexes.has(item.index)
                  ? item.media.video.mp4Url
                  : undefined
              }
              poster={item.media.video.posterUrl}
              loop
              muted
              playsInline
              preload="none"
              data-gallery-video="true"
            />
          ) : item.index === currentIndex ? (
            <ResponsiveImage
              rootClassName={css.item}
              image={item.image}
              alt={item.alt}
              variants={imageVariants}
              sizes="(max-width: 767px) 100vw, 80vw"
              onLoad={() => markStillLoaded(stillImageKey(item.image) || `index:${item.index}`)}
              onError={() => markStillLoaded(stillImageKey(item.image) || `index:${item.index}`)}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderThumbInner = item => {
    if (item.media?.type === MEDIA_TYPE_VIDEO) {
      return <img className={css.thumb} src={item.media.video.posterUrl} alt={item.thumbAlt} />;
    }
    return (
      <ResponsiveImage
        rootClassName={css.thumb}
        image={item.image}
        alt={item.thumbAlt}
        variants={thumbVariants}
        sizes="72px"
      />
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        type="button"
        className={css.navLeft}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ImageCarousel.previousImage' })}
      >
        <span className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="small" className={css.arrowHead} />
        </span>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        type="button"
        className={css.navRight}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ImageCarousel.nextImage' })}
      >
        <span className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="small" className={css.arrowHead} />
        </span>
      </button>
    );
  };

  // Catch heroes that decoded before hydration, when onLoad will never fire.
  useLayoutEffect(() => {
    const item = items[currentIndex];
    const key = item?.image ? stillImageKey(item.image) || `index:${currentIndex}` : null;
    if (key && currentHeroAlreadyLoaded(galleryRootRef.current)) {
      markStillLoaded(key);
    }
  }, [items, currentIndex, markStillLoaded]);

  if (items.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={classes} image={null} variants={[]} alt="" />;
  }

  // onBeforeSlide fires when the index changes; onSlide waits until after the
  // transition and can leave the counter behind the active thumbnail.
  const handleBeforeSlide = nextIndex => {
    setIndex(nextIndex);
  };

  const classes = classNames(rootClassName || css.root, className);

  const currentItem = items[currentIndex];
  const currentStillKey = currentItem?.image
    ? stillImageKey(currentItem.image) || `index:${currentIndex}`
    : null;
  const heroLoading =
    !!currentItem?.image &&
    currentItem.media?.type !== MEDIA_TYPE_VIDEO &&
    stillHasRequestedVariant(currentItem.image, imageVariants) &&
    !loadedStillKeys.has(currentStillKey);

  return (
    <div className={css.lightbox} ref={galleryRootRef}>
      <span className={css.imageIndex}>
        {currentIndex + 1} / {items.length}
      </span>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onBeforeSlide={handleBeforeSlide}
        startIndex={initialIndex}
        showThumbnails={showThumbnails}
        {...IMAGE_GALLERY_OPTIONS}
      />
      <HeroLoadSpinner active={heroLoading} containerRef={galleryRootRef} />
    </div>
  );
};

export default ImageCarousel;
